import React, { useState, useEffect } from 'react';
import FormInput from '../common/FormInput';
import FormButton from '../common/FormButton';
import { useAuth } from '../../../contexts/AuthContext';

const LoginForm = ({ onNavigate }) => {
  const { login, previousPage, googleLogin } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    
    try {
      await login(formData);
      // Login successful, navigate back to previous page or main page
      onNavigate(previousPage || 'main');
    } catch (error) {
      const oauthCode =
        error.type === 'oauth_account' ||
        error.details?.data?.errors?.code === 'oauth_account';
      if (oauthCode) {
        setErrors({
          general: error.message,
          oauthAccount: true,
        });
      } else if (error.type === 'validation_error') {
        setErrors(error.errors);
      } else {
        setErrors({ general: error.message || 'Invalid email or password. Please try again.' });
      }
    } finally {
      setLoading(false);
    }
  };


  // Initialize Google OAuth2 token client
  useEffect(() => {
    const initializeGoogleSignIn = () => {
      if (window.google && window.google.accounts && window.google.accounts.oauth2) {
        // Google Identity Services is loaded
        // We'll use OAuth2 token client when button is clicked
      } else {
        // Retry if Google script hasn't loaded yet
        setTimeout(initializeGoogleSignIn, 100);
      }
    };

    initializeGoogleSignIn();
  }, []);

  // This function is no longer needed as we use renderButton()
  // Keeping it for backward compatibility but it won't be called
  const handleGoogleLogin = () => {
    if (!window.google || !window.google.accounts || !window.google.accounts.oauth2) {
      setErrors({ general: 'Google sign-in is not available. Please refresh the page.' });
      return;
    }

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId) {
      setErrors({ general: 'Google OAuth is not configured. Please contact support.' });
      return;
    }

    try {
      setGoogleLoading(true);
      setErrors({});

      // Use OAuth2 token client to get access_token (what django-allauth expects)
      const tokenClient = window.google.accounts.oauth2.initTokenClient({
        client_id: clientId,
        scope: 'openid email profile',
        callback: async (tokenResponse) => {
          if (tokenResponse.error) {
            setErrors({ general: 'Google sign-in was cancelled or failed.' });
            setGoogleLoading(false);
            return;
          }

          if (tokenResponse.access_token) {
            try {
              // Call backend with access_token
              await googleLogin(tokenResponse.access_token);
              
              // Navigate after successful login
              onNavigate(previousPage || 'main');
            } catch (error) {
              console.error('Google login error:', error);
              setErrors({ 
                general: error.message || 'Google login failed. Please try again.' 
              });
            } finally {
              setGoogleLoading(false);
            }
          } else {
            setErrors({ general: 'Failed to get Google access token.' });
            setGoogleLoading(false);
          }
        },
      });

      // Request access token
      tokenClient.requestAccessToken({ prompt: 'consent' });
    } catch (error) {
      console.error('Google OAuth initialization error:', error);
      setErrors({ general: 'Failed to initialize Google sign-in. Please try again.' });
      setGoogleLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errors.general && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {errors.general}
          {errors.oauthAccount && (
            <p className="mt-2 text-sm">
              Sign in with Google below, then set a password under Profile → Security if you want email sign-in.
            </p>
          )}
        </div>
      )}

      <FormInput
        label="Email Address"
        type="email"
        name="email"
        value={formData.email}
        onChange={handleChange}
        placeholder="Enter your email"
        required
        error={errors.email}
      />

      <FormInput
        label="Password"
        type="password"
        name="password"
        value={formData.password}
        onChange={handleChange}
        placeholder="Enter your password"
        required
        error={errors.password}
      />

      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => onNavigate('forgot-password')}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
        >
          Forgot your password?
        </button>
      </div>

      <FormButton
        type="submit"
        loading={loading}
        disabled={loading}
      >
        Sign In
      </FormButton>

      <div className="text-center">
        <p className="text-gray-600">
          Don't have an account?{' '}
          <button
            type="button"
            onClick={() => onNavigate('register')}
            className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            Sign up
          </button>
        </p>
      </div>

      {/* Google OAuth Button */}
      <div className="mt-6">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-white text-gray-500">Or continue with</span>
          </div>
        </div>

        <div className="mt-6">
          <button
            type="button"
            disabled={googleLoading || loading}
            className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleGoogleLogin}
          >
            {googleLoading ? (
              <span>Signing in with Google...</span>
            ) : (
              <>
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                Continue with Google
              </>
            )}
          </button>
        </div>
      </div>
    </form>
  );
};

export default LoginForm;
